import {
  getOddsFormat,
  probabilityToAmerican,
  probabilityToDecimal,
  probabilityToFractional,
} from '@/lib/oddsConversion';

export function fmtProbFromCents(cents: number | null): string {
  if (cents === null) return '—';
  const c = Number(cents);
  if (!Number.isFinite(c)) return '—';
  return `${Math.round(c)}¢`;
}

export function centsToAmericanOdds(cents: number | null): string {
  if (cents === null) return '—';
  const prob = Number(cents) / 100;
  if (!Number.isFinite(prob) || prob <= 0 || prob >= 1) return '—';
  const oneOverProb = 1 / prob;
  let odds: number;
  if (oneOverProb > 2) odds = (oneOverProb - 1) * 100;
  else odds = -100 / (oneOverProb - 1);
  const rounded = Math.round(odds);
  const formatted = Math.abs(rounded).toLocaleString();
  return rounded > 0 ? `+${formatted}` : `-${formatted}`;
}

/**
 * Format a price in cents according to the user's selected odds format.
 *
 * - probability: 46¢
 * - american:    +117
 * - decimal:     2.17
 * - fractional:  27/23
 *
 * Falls back to cents display for edge-case values (0, 100, NaN).
 */
export function formatPriceCents(cents: number | null): string {
  if (cents === null) return '—';
  const c = Number(cents);
  if (!Number.isFinite(c)) return '—';

  const format = getOddsFormat();
  const prob = c / 100;

  switch (format) {
    case 'probability':
      return `${Math.round(c)}¢`;

    case 'american': {
      if (prob <= 0 || prob >= 1) return `${Math.round(c)}¢`;
      const odds = probabilityToAmerican(prob);
      return odds > 0 ? `+${odds}` : `${odds}`;
    }

    case 'decimal': {
      if (prob <= 0 || prob >= 1) return `${Math.round(c)}¢`;
      return probabilityToDecimal(prob).toFixed(2);
    }

    case 'fractional': {
      if (prob <= 0 || prob >= 1) return `${Math.round(c)}¢`;
      try {
        return probabilityToFractional(prob);
      } catch {
        return `${Math.round(c)}¢`;
      }
    }

    default:
      return `${Math.round(c)}¢`;
  }
}

/** @deprecated Use formatPriceCents — respects user's odds format preference */
export const formatDualPriceCents = formatPriceCents;
